import { LegacyModalViewDefinition, ViewAssociation } from "./types";
import { v4 as uuidv4 } from "uuid";
import {
	isCloseFloatingViewEvent,
	isCloseUppermostFloatingViewEvent,
	isOpenDynamicViewEvent,
	isOpenFloatingViewEvent,
	isOpenFrontendModalViewEvent,
	OPEN_FLOATING_VIEW_ACTION_SPECIFIER,
	VersionedViewActionEventList,
	ViewActionEvent,
} from "./events";
import {
	getDynamicViewDefinitionFromEvent,
	getModalViewParamsFromEvent,
	getViewOrErrorFromStartUpData,
	isErrorViewDefinition,
} from "src/components/views/legacy/utils";
import { StartUpData } from "src/api/mainApi";
import React from "react";
import { KeyValuePair } from "src/utils/keyValuePair.ts";
import { logError } from "src/errorHandling/errorLogging.ts";
import { FrontendViewConfig } from "src/components/views/frontendViews/frontendViewConfig.ts";
import { isImplementedFrontendViewId } from "src/components/views/frontendViews/frontendViewUtils.tsx";
import { FRONTEND_VIEWS_BY_KEYS } from "src/components/views/frontendViews/frontendViewsList.ts";

interface UUIDField {
	uuid: string;
}

interface ViewParamsField {
	viewParams: KeyValuePair[];
}

interface LegacyModalViewState
	extends Omit<LegacyModalViewDefinition, "isUppermostView">,
		UUIDField {
	view: ViewAssociation;
}

export type AavoViewsContextState = {
	viewIdsOpened: string[];
	frontendViewsOpened: string[];
	legacyModals: Array<LegacyModalViewState>;
	frontendViewModals: Array<FrontendViewConfig & UUIDField & ViewParamsField>;
	test?: string;
	viewActionEvents: VersionedViewActionEventList;
	dragDropData: any;
};

const AAVO_VIEWS_CONTEXT_DEFAULT: AavoViewsContextState = {
	viewIdsOpened: [],
	frontendViewsOpened: [],
	frontendViewModals: [],
	viewActionEvents: {
		version: 0,
		events: [],
	},
	legacyModals: [],
	dragDropData: undefined,
};

export type Action =
	| {
			type: "addActiveViewId";
			viewId: string;
	  }
	| {
			type: "addActiveFrontendView";
			viewKey: string;
	  }
	| {
			type: "setViewActionEvents";
			events: Array<ViewActionEvent>;
			startUpData: StartUpData;
	  }
	| {
			type: "openLegacyModal";
			modalViewState: Omit<LegacyModalViewState, "uuid">;
	  }
	| {
			type: "closeLegacyModalWithUuid";
			uuid: string;
	  }
	| {
			type: "setLegacyModalWithUuidUppermost";
			uuid: string;
	  }
	| {
			type: "closeLegacyModalsWithId";
			viewId: string;
	  }
	| {
			type: "closeUppermostLegacyModal";
	  }
	| {
			type: "setDragDropData";
			dragDropData: any;
	  }
	| {
			type: "openFrontendModal";
			viewId: string;
			viewParams: KeyValuePair[];
	  }
	| {
			type: "closeFrontendModalWithUuid";
			uuid: string;
	  };

type Dispatch = (action: Action) => void;

export const AavoViewsContext = React.createContext<
	| {
			state: AavoViewsContextState;
			dispatch: Dispatch;
	  }
	| undefined
>(undefined);

export const AavoViewsDispatchContext = React.createContext<Dispatch | undefined>(undefined);

type AavoViewsProviderProps = {
	children: React.ReactNode;
};

type AavoViewsReducerFunction = (
	state: AavoViewsContextState,
	action: Action,
) => AavoViewsContextState;

const aavoViewsReducer: AavoViewsReducerFunction = (state, action) => {
	switch (action.type) {
		case "closeFrontendModalWithUuid":
			return {
				...state,
				frontendViewModals: excludeItemWithUuid(action.uuid, state.frontendViewModals),
			};
		case "openFrontendModal":
			return handleOpenFrontendModal(action.viewId, action.viewParams, state);
		case "setDragDropData":
			return {
				...state,
				dragDropData: action.dragDropData,
			};
		case "closeUppermostLegacyModal":
			return {
				...state,
				legacyModals: state.legacyModals.slice(1),
			};
		case "closeLegacyModalsWithId":
			return {
				...state,
				legacyModals: excludeModalsWithViewId(action.viewId, state.legacyModals),
			};
		case "openLegacyModal":
			return {
				...state,
				legacyModals: [{ ...action.modalViewState, uuid: uuidv4() }, ...state.legacyModals],
			};
		case "closeLegacyModalWithUuid":
			return {
				...state,
				legacyModals: excludeItemWithUuid(action.uuid, state.legacyModals),
			};
		case "setLegacyModalWithUuidUppermost":
			return {
				...state,
				legacyModals: modalToUppermost(action.uuid, state.legacyModals),
			};
		case "setViewActionEvents":
			return handleSetActionViewEvents(action.events, action.startUpData, state);
		case "addActiveViewId":
			return state.viewIdsOpened.includes(action.viewId)
				? state
				: {
						...state,
						viewIdsOpened: [action.viewId, ...state.viewIdsOpened],
				  };
		case "addActiveFrontendView":
			return state.frontendViewsOpened.includes(action.viewKey)
				? state
				: {
						...state,
						frontendViewsOpened: [action.viewKey, ...state.frontendViewsOpened],
				  };
	}
};

export function AavoViewsProvider({ children }: AavoViewsProviderProps) {
	const [state, dispatch] = React.useReducer(aavoViewsReducer, AAVO_VIEWS_CONTEXT_DEFAULT);
	const value = { state, dispatch };
	return (
		<AavoViewsContext.Provider value={value}>
			<AavoViewsDispatchContext.Provider value={dispatch}>
				{children}
			</AavoViewsDispatchContext.Provider>
		</AavoViewsContext.Provider>
	);
}

const excludeItemWithUuid = <T extends UUIDField>(uuid: string, items: T[]): T[] => {
	return items.filter((item) => {
		return item.uuid !== uuid;
	});
};

const excludeModalsWithViewId = (viewId: string, modals: LegacyModalViewState[]) => {
	return modals.filter((modal) => {
		return modal.view.view.id !== viewId;
	});
};

const modalToUppermost = (uuid: string, modals: LegacyModalViewState[]): LegacyModalViewState[] => {
	const newUppermost = modals.find((modal) => {
		return modal.uuid === uuid;
	});
	if (newUppermost) {
		return [newUppermost, ...excludeItemWithUuid(uuid, modals)];
	} else {
		console.error("Tried to set non-existing aavo modal view to uppermost view.");
		return modals;
	}
};

const handleSetActionViewEvents = (
	events: Array<ViewActionEvent>,
	startUpData: StartUpData,
	prevState: AavoViewsContextState,
): AavoViewsContextState => {
	const stateWithEvents: AavoViewsContextState = {
		...prevState,
		viewActionEvents: {
			version: prevState.viewActionEvents.version + 1,
			events: events,
		},
	};
	return events.reduce((prev, e) => {
		return updateModalsAfterEvent(e, prev, startUpData);
	}, stateWithEvents);
};

const updateModalsAfterEvent = (
	e: ViewActionEvent,
	state: AavoViewsContextState,
	startUpData: StartUpData,
) => {
	if (isOpenFloatingViewEvent(e)) {
		return openLegacyModal(e, e.action.target.viewId, startUpData, state);
	} else if (isOpenDynamicViewEvent(e)) {
		return aavoViewsReducer(state, {
			type: "openLegacyModal",
			modalViewState: {
				sourceViewId: e.senderViewId,
				viewParams: getModalViewParamsFromEvent(e),
				view: getDynamicViewDefinitionFromEvent(e),
			},
		});
	} else if (isCloseUppermostFloatingViewEvent(e)) {
		return aavoViewsReducer(state, {
			type: "closeUppermostLegacyModal",
		});
	} else if (isCloseFloatingViewEvent(e)) {
		return aavoViewsReducer(state, {
			type: "closeLegacyModalsWithId",
			viewId: getActionTargetViewId(e),
		});
	} else if (isOpenFrontendModalViewEvent(e)) {
		const frontendViewKey = e.action.target.viewId;

		if (isImplementedFrontendViewId(frontendViewKey)) {
			return aavoViewsReducer(state, {
				type: "openFrontendModal",
				viewId: e.action.target.viewId,
				viewParams: e.allParams,
			});
		} else {
			return openLegacyModal(
				e,
				e.action.params.find((param: any) => param.key === "__FALLBACK_VIEW_ID__").value,
				startUpData,
				state,
			);
		}
	} else {
		return { ...state };
	}
};

const getActionTargetViewId = (event: ViewActionEvent) => {
	if (event.action.target.type === "view") return event.action.target.viewId;
	if (event.action.target.type === "self") return event.senderViewId;
	if (event.action.target.type === "sourceView") return event.senderSourceViewId;

	return undefined;
};

const openLegacyModal = (
	e: ViewActionEvent,
	viewId: string,
	startUpData: StartUpData,
	state: AavoViewsContextState,
): AavoViewsContextState => {
	const viewDefinition = getViewOrErrorFromStartUpData(viewId, startUpData);
	if (isErrorViewDefinition(viewDefinition)) {
		console.warn(
			`Action event ${OPEN_FLOATING_VIEW_ACTION_SPECIFIER} raised with invalid view id ${viewId}. The view does not exist in startUpData.`,
		);
	}

	return aavoViewsReducer(state, {
		type: "openLegacyModal",
		modalViewState: {
			sourceViewId: e.senderViewId,
			viewParams: getModalViewParamsFromEvent(e),
			view: viewDefinition,
		},
	});
};

const handleOpenFrontendModal = (
	viewId: string,
	viewParams: KeyValuePair[],
	prevState: AavoViewsContextState,
): AavoViewsContextState => {
	const frontendViewConfig = FRONTEND_VIEWS_BY_KEYS[viewId];
	if (frontendViewConfig !== undefined) {
		return {
			...prevState,
			frontendViewModals: [
				{ ...frontendViewConfig, viewParams: viewParams, uuid: uuidv4() },
				...prevState.frontendViewModals,
			],
		};
	} else {
		logError(`Trying to open unimplemented frontend view ${viewId}`);
	}
	return { ...prevState };
};
